import React, {MouseEvent, ReactNode, RefObject} from "react";
import FunctionHelper from "../helper/FunctionHelper";
import PortalPureComponent from "./PortalPureComponent";

export enum ResizeableDirection {
	HORIZONTAL = "horizontal",
	VERTICAL = "vertical",
	BOTH = "both"
}

interface ResizeableProperties {
	direction: ResizeableDirection;
	resizeCallback: (options: { width: string, height: string }) => void;
	container: RefObject<any>;
	invert: boolean;
	minHeight?: number;
	maxHeight?: number;
	minWidth?: number;
	maxWidth?: number;
}

interface ResizeableState {
	active: boolean;
}

export default class Resizeable extends PortalPureComponent<ResizeableProperties, ResizeableState> {
	constructor(props: Readonly<ResizeableProperties>) {
		super(props);

		this.state = {
			active: false
		};

		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.handleMouseUp = this.handleMouseUp.bind(this);
	}

	private handleMouseMove(event: MouseEvent): void {
		if (!this.state.active) {
			return;
		}

		if (!event.buttons) {
			this.setState({
				active: false
			});
		}

		let bounds = this.props.container.current.getBoundingClientRect();
		let width = event.pageX - bounds.left;
		let height = event.pageY - bounds.top;

		if (this.props.invert) {
			width = bounds.left + bounds.width - event.pageX;
			height = bounds.top + bounds.height - event.pageY;
		}

		let resultHeight: number = (100 * height / bounds.height);

		if (FunctionHelper.exists(this.props.minHeight)) {
			resultHeight = Math.max(resultHeight, this.props.minHeight!);
		}

		if (FunctionHelper.exists(this.props.maxHeight)) {
			resultHeight = Math.min(resultHeight, this.props.maxHeight!);
		}

		let resultWidth: number = (100 * width / bounds.width);

		if (FunctionHelper.exists(this.props.minWidth)) {
			resultWidth = Math.max(resultWidth, this.props.minWidth!);
		}

		if (FunctionHelper.exists(this.props.maxWidth)) {
			resultWidth = Math.min(resultWidth, this.props.maxWidth!);
		}

		switch (this.props.direction) {
			case ResizeableDirection.VERTICAL:

				this.props.resizeCallback({
					height: resultHeight + '%',
					width: "auto"
				});
				break;
			case ResizeableDirection.HORIZONTAL:
				this.props.resizeCallback({
					height: "auto",
					width: resultWidth + '%'
				});
				break;
			case ResizeableDirection.BOTH:
				this.props.resizeCallback({
					height: resultHeight + '%',
					width: resultWidth + '%'
				});
				break;
		}
	}

	private handleMouseDown(event: React.MouseEvent): void {
		if (event.button !== 0) {
			return;
		}

		this.setState({
			active: true
		});
	}

	private handleMouseUp(event: MouseEvent): void {
		if (event.button !== 0) {
			return;
		}

		this.setState({
			active: false
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<div
				className={
					"resizeable " + this.props.direction + (this.state.active ? " is-active" : "") +
					(this.props.invert ? " is-inverted" : "")
				}
				onPointerDown={this.handleMouseDown}>
				{this.state.active &&
					<div className={"resize-overlay is-" + this.props.direction} onPointerMove={this.handleMouseMove}
						 onPointerUp={this.handleMouseUp}/>
				}
			</div>
		);
	}
}